import { armySize, unitAmounts, upkeepOf, upkeepScale } from '@/game/sim';
import { ownCities, stockLabel } from '@/game/view/cities';
import { type IncomeTracker, rateLabel, sampleIncome, starvingIn } from '@/game/view/income';
import { clockLabel } from '@/game/view/matchClock';
import { labelOrDefault } from '@/game/view/names';
import {
  RESOURCES_SHARED_TEXT,
  resourceIcon,
  shownResources,
  stockInUnits,
} from '@/game/view/resources';
import { liveScore, liveScoreTitle } from '@/game/view/score';
import { useSimFrame } from '../app/simFrame';
import type { SpendFlash } from '../app/useCommandFeedback';
import { spendLabel, sumSpends } from './spend';

/**
 * The player's resources (in the top bar). They live in cities but are spent
 * as one pool: it shows the total; its tooltip says so and lists each
 * city's share, the army's upkeep and the live score. Each total has its net
 * income per second beside it (red when falling; view/income.ts), then the
 * army's upkeep, and a "starving in M:SS" chip while food is running out.
 * Below it, what clicks that just landed spent ("−20🌾 −12🪵", `spends`), for a moment:
 * the balance itself is only ever the host's.
 */
export function ResourcesPill({
  spends = [],
  tracker,
}: {
  spends?: readonly SpendFlash[];
  /** The stock samples the rates are measured from; kept by the parent so a remount doesn't reset them. */
  tracker: IncomeTracker;
}) {
  const { view: state, real, playerId, rules, cityNumbers, names } = useSimFrame();
  const rawStockpile = stockInUnits(state, playerId);
  // Metal joins food and wood once the player has some or reaches its era.
  const shown = shownResources(state, rules, playerId, rawStockpile);
  // Upkeep (food/s for military units, scaled by army size).
  const upkeep = upkeepOf(state, rules, playerId);
  const army = armySize(state, rules, playerId);
  const upkeepRates = Object.entries(upkeep)
    .filter(([, rate]) => rate > 0)
    .map(([res, rate]) => `${rate.toFixed(2)} ${rules.resources[res]?.name.toLowerCase() ?? res}/s`)
    .join(', ');
  const upkeepText =
    upkeepRates &&
    `${upkeepRates} (×${upkeepScale(rules, army).toFixed(2)} for ${army} soldier${army === 1 ? '' : 's'})`;
  // The real state, so a rival spy disguised as one of theirs doesn't count.
  const score = liveScore(real, rules, playerId);
  const spent = spends.length > 0 ? spendLabel(sumSpends(spends.map((f) => f.amounts))) : '';
  // Sampled every frame: the rate is measured over a window of stock samples.
  const rates = sampleIncome(tracker, state, rules, playerId);
  const starving = starvingIn(state, rules, playerId, rates.food ?? null);
  const completedCities = ownCities(state, rules, playerId).sort(
    (a, b) => (cityNumbers.get(a.id) ?? 0) - (cityNumbers.get(b.id) ?? 0)
  );

  return (
    <div
      className="relative flex shrink-0 items-center gap-3 text-sm tabular-nums"
      title={[
        RESOURCES_SHARED_TEXT,
        '',
        'Held in each city:',
        ...completedCities.map(
          (city) =>
            `  ${labelOrDefault(names, city.id, `City ${cityNumbers.get(city.id) ?? '?'}`)}: ${stockLabel(unitAmounts(city.stock ?? {}))}`
        ),
        ...(upkeepText ? ['', `Army upkeep: ${upkeepText}`] : []),
        '',
        liveScoreTitle(score),
      ].join('\n')}
    >
      {shown.map((res) => {
        const rate = rates[res] ?? null;
        return (
          <span key={res} className="flex items-baseline gap-1 whitespace-nowrap">
            {resourceIcon(res)}
            {/* Fixed minimums, so the chips after them don't move as digits or the rate change. */}
            <span className="inline-block min-w-10">{Math.floor(rawStockpile[res] ?? 0)}</span>
            <span
              className={`inline-block min-w-11 text-[11px] ${rate === null || Math.abs(rate) < 0.05 ? 'text-fg-subtle' : rate < 0 ? 'text-danger' : 'text-ok'}`}
            >
              {rate === null ? '' : rateLabel(rate)}
            </span>
          </span>
        );
      })}
      {Object.entries(upkeep)
        .filter(([, rate]) => rate > 0)
        .map(([res, rate]) => (
          <span
            key={res}
            className="chip whitespace-nowrap text-[11px] text-fg-muted"
            title={`Army upkeep: ${upkeepText}`}
          >
            upkeep {Object.keys(upkeep).length > 1 ? resourceIcon(res) : ''}
            {rateLabel(-rate)}
          </span>
        ))}
      {starving !== null && (
        <span
          role="alert"
          title="Units start starving when food runs out and the grace period ends"
          className="chip whitespace-nowrap bg-danger-surface text-[11px] font-medium text-danger"
        >
          starving in {clockLabel(Math.ceil(starving))}
        </span>
      )}
      {spent && (
        <span
          key={spends[spends.length - 1]?.id}
          role="status"
          className="pill pointer-events-none absolute left-0 top-full z-20 mt-2.5 whitespace-nowrap px-3 py-0.5 text-xs text-warn"
        >
          {spent}
        </span>
      )}
    </div>
  );
}
